import {_} from 'lodash';
import * as ko from "knockout";
import {AbstractMutableData} from "../AbstractMutableData";
import {INPUT_DATE_FORMAT} from "../../../../../constants";
import {ICompany} from "../../../../../index";
import {nsUtils} from "../../nsUtils";
import {nsDate} from "../../nsDate";
import {InputObject} from "../../InputObject";
import {StatusManager} from "../../StatusManager";
import {moment} from 'moment';

export class MutableNotificationData extends AbstractMutableData {

    public readonly syncerrorreportrecipients: KnockoutObservableArray<InputObject<string>> = ko.observableArray<InputObject<string>>([])
    public readonly syncendreportrecipients: KnockoutObservableArray<InputObject<string>> = ko.observableArray<InputObject<string>>([])
    public input_daystosyncstopwarning: InputObject<number>
    public input_sendwarningonstop: InputObject<boolean>
    public syncstopwarningdeactivated: KnockoutObservable<string | null> = ko.observable(null)
    public syncstopwarningdeactivatedby: KnockoutObservable<number | null> = ko.observable(null)
    public readonly isEndReportActive: KnockoutObservable<boolean | null> = ko.observable(null)
    private isErrorReportActive_original!: boolean
    private isEndReportActive_original!: boolean

    constructor(statusManager: StatusManager) {
        super(statusManager)

        this.input_daystosyncstopwarning = new InputObject<number>({
            value: 0,
            type: 'number',
            attr: {
                min: 0,
                max: 30,
            },
            ph: 'Day|p',
            isValid: num => !isNaN(num) && num >= 0 && num <= 30 && parseInt(String(num)) == num,
            event: {
                blur: obj => obj.obs(obj.obs() || 1),
            }
        })

        this.input_sendwarningonstop = new InputObject<boolean>({
            value: false,
            type: 'checkbox',
        })

    }

    public override init() {
        this.isErrorReportActive_original = !this.statusManager.company.syncstopwarningdeactivatedby
        this.isActive(this.isErrorReportActive_original)

        this.isEndReportActive_original = !this.statusManager.company.syncendreportsdeactivatedby
        this.isEndReportActive(this.isEndReportActive_original)

        this.syncerrorreportrecipients([])
        this.statusManager.company.syncerrorreportrecipients
            ?.split(',')
            .filter(Boolean)
            .forEach(email => this.addErrorEmail(email))
        if (this.syncerrorreportrecipients().length === 0) {
            this.addErrorEmail()
        }

        this.syncendreportrecipients([])
        this.statusManager.company.syncendreportrecipients
            ?.split(',')
            .filter(Boolean)
            .forEach(email => this.addEndEmail(email))
        if (this.syncendreportrecipients().length === 0) {
            this.addEndEmail()
        }

        const m = moment(this.statusManager.company.syncstopwarningdeactivated, 'DD.MM.YYYY HH:mm');
        this.input_date(m.format(INPUT_DATE_FORMAT))
        this.input_daystosyncstopwarning.obs(this.statusManager.company.daystosyncstopwarning || 0)
        this.input_sendwarningonstop.obs(this.statusManager.company.sendwarningonstop)
        this.syncstopwarningdeactivated(this.statusManager.company.syncstopwarningdeactivated)
        this.syncstopwarningdeactivatedby(this.statusManager.company.syncstopwarningdeactivatedby)

        super.init();
    }

    public addErrorEmail(email?: string) {
        this.syncerrorreportrecipients().forEach(o => {
            const ed = (o as any).isEditing as KnockoutObservable<boolean> | undefined
            if (ed?.() && o.isValid()) {
                ed(false)
            }
        })

        const obj = new InputObject<string>({
            value: email ?? '',
            type: 'email',
            isValid: str => nsUtils.validateEmail(str),
            ph: 'Email',
        })
        ;(obj as any).isEditing = ko.observable(!email)
        this.syncerrorreportrecipients.push(obj)
        return obj
    }

    public removeErrorEmail(row?: InputObject<string>) {
        if (row) {
            this.syncerrorreportrecipients.remove(row)
        }
        if (this.syncerrorreportrecipients().length === 0) {
            this.addErrorEmail()
        }
    }

    public addEndEmail(email?: string) {
        this.syncendreportrecipients().forEach(o => {
            const ed = (o as any).isEditing as KnockoutObservable<boolean> | undefined
            if (ed?.() && o.isValid()) {
                ed(false)
            }
        })

        const obj = new InputObject<string>({
            value: email ?? '',
            type: 'email',
            isValid: str => nsUtils.validateEmail(str),
            ph: 'Email',
        })
        ;(obj as any).isEditing = ko.observable(!email)
        this.syncendreportrecipients.push(obj)
        return obj
    }

    public removeEndEmail(row?: InputObject<string>) {
        if (row) {
            this.syncendreportrecipients.remove(row)
        }
        if (this.syncendreportrecipients().length === 0) {
            this.addEndEmail()
        }
    }

    /** + control: show only when every row is a chip (valid email committed via blur/Enter). */
    public readonly showAddErrorRecipient = ko.pureComputed(() => {
        const rows = this.syncerrorreportrecipients()
        return rows.length > 0 && rows.every(MutableNotificationData.isCommittedRecipientRow)
    })

    public readonly showAddEndRecipient = ko.pureComputed(() => {
        const rows = this.syncendreportrecipients()
        return rows.length > 0 && rows.every(MutableNotificationData.isCommittedRecipientRow)
    })

    private static isCommittedRecipientRow(o: InputObject<string>): boolean {
        const ed = (o as any).isEditing as KnockoutObservable<boolean> | undefined
        if (!ed) {
            return o.isValid()
        }
        return !ed() && o.isValid()
    }

    public getSubmitData = ko.pureComputed((): ICompany => {
        const isActiveChanged = this.isActive() != this.isErrorReportActive_original
        let syncstopwarningdeactivated: string
        let syncstopwarningdeactivatedby: number
        let syncstopwarningdeactivatedbyorgnr: string

        if (!isActiveChanged) {
            syncstopwarningdeactivated = this.statusManager.company['syncstopwarningdeactivated'] ?? ''
            syncstopwarningdeactivatedby = this.statusManager.company['syncstopwarningdeactivatedby'] ?? 0
            syncstopwarningdeactivatedbyorgnr = this.statusManager.company['syncstopwarningdeactivatedbyorgnr'] ?? ''
        } else if (!this.isActive()) {
            syncstopwarningdeactivated = nsDate.convertToServerString(moment())
            syncstopwarningdeactivatedby = this.loginObj.id
            syncstopwarningdeactivatedbyorgnr = this.loginObj.orgnr
        } else {
            syncstopwarningdeactivated = ''
            syncstopwarningdeactivatedby = 0
            syncstopwarningdeactivatedbyorgnr = ''
        }

        const isEndReportActiveChanged = this.isEndReportActive() != this.isEndReportActive_original
        let syncendreportsdeactivated: string
        let syncendreportsdeactivatedby: number
        let syncendreportsdeactivatedbyorgnr: string

        if (!isEndReportActiveChanged) {
            syncendreportsdeactivated = this.statusManager.company['syncendreportsdeactivated'] ?? ''
            syncendreportsdeactivatedby = this.statusManager.company['syncendreportsdeactivatedby'] ?? 0
            syncendreportsdeactivatedbyorgnr = this.statusManager.company['syncendreportsdeactivatedbyorgnr'] ?? ''
        } else if (!this.isEndReportActive()) {
            syncendreportsdeactivated = nsDate.convertToServerString(moment())
            syncendreportsdeactivatedby = this.loginObj.id
            syncendreportsdeactivatedbyorgnr = this.loginObj.orgnr
        } else {
            syncendreportsdeactivated = ''
            syncendreportsdeactivatedby = 0
            syncendreportsdeactivatedbyorgnr = ''
        }

        const ret: Partial<ICompany> = {
            daystosyncstopwarning: parseInt(String(this.input_daystosyncstopwarning.obs())),
            syncerrorreportrecipients: this.syncerrorreportrecipients()
                .map(o => o.obs())
                .filter(Boolean)
                .join(','),
            syncendreportrecipients: this.syncendreportrecipients()
                .map(o => o.obs())
                .filter(Boolean)
                .join(','),
            syncstopwarningdeactivated,
            syncstopwarningdeactivatedby,
            syncstopwarningdeactivatedbyorgnr,
            syncendreportsdeactivated,
            syncendreportsdeactivatedby,
            syncendreportsdeactivatedbyorgnr,
            sendwarningonstop: this.input_sendwarningonstop.obs() ?? false,
        }

        return _.defaultsDeep(ret, this.mutableCompany.getSubmitData())
    })

    public isChanged: KnockoutComputed<boolean> = ko.pureComputed(() => {
        const a = this.getSubmitData()['daystosyncstopwarning'] != this.statusManager.company['daystosyncstopwarning']
        const b = this.getSubmitData()['syncerrorreportrecipients'] != this.statusManager.company['syncerrorreportrecipients']
        const c = this.getSubmitData()['sendwarningonstop'] != this.statusManager.company['sendwarningonstop']
        const d = this.isActive() != this.isErrorReportActive_original
        const e = this.getSubmitData()['syncendreportrecipients'] != this.statusManager.company['syncendreportrecipients']
        const f = this.isEndReportActive() != this.isEndReportActive_original
        return a || b || c || d || e || f
    })

    public isValid = ko.pureComputed(() => {
        const a = this.isInputValid('daystosyncstopwarning')
        const b = this.isInputValid('syncerrorreportrecipients')
        const c = this.isInputValid('syncendreportrecipients')
        return a && b && c
    })

    public isInputValid(key: keyof ICompany): boolean {
        switch (key) {
            case 'daystosyncstopwarning': {
                return this.input_daystosyncstopwarning.isValid()
            }
            case 'syncerrorreportrecipients': {
                return this.syncerrorreportrecipients().every(o => o.isValid())
            }
            case 'syncendreportrecipients': {
                return this.syncendreportrecipients().every(o => o.isValid())
            }
            default:
                return true
        }
    }

    public getErrorEmailTitle(): string {
        return this.syncerrorreportrecipients().map(o => o.obs()).join('\n')
    }

    public getEndEmailTitle(): string {
        return this.syncendreportrecipients().map(o => o.obs()).join('\n')
    }

}
